import React, { lazy, useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useParams } from "react-router-dom";
import SecureLS from "secure-ls";
import RouteSuspense from "./RouteSuspense";
import { getRouteSegmentForPosId, resolvePosId } from "../utils/posIdUtils";

const StoreRedirect = lazy(() => import("../pages/StoreRedirect"));

const ls = new SecureLS({ encodingType: "aes" });

const normalizeLocalHost = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const candidate = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
    const url = new URL(candidate);
    return String(url.hostname || "").trim().toLowerCase();
  } catch {
    return raw
      .replace(/^https?:\/\//i, "")
      .split("/")[0]
      .split(":")[0]
      .trim()
      .toLowerCase();
  }
};

const buildLocalAssistedUrl = ({ localIp, routeSegment, wantsOrder, isMobile }) => {
  const rawLocalIp = String(localIp || "").trim();
  const segment = String(routeSegment || "").trim();
  if (!rawLocalIp || !segment) return "";

  try {
    const candidate = /^https?:\/\//i.test(rawLocalIp)
      ? rawLocalIp
      : `http://${rawLocalIp}`;
    const url = new URL(candidate);
    if (!url.port) url.port = "3000";
    const basePath = isMobile ? `/mobile/${segment}` : `/${segment}`;
    url.pathname = wantsOrder ? `${basePath}/order` : basePath;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
};

const StoreGuard = ({ wantsOrder = false, isMobile = false, children }) => {
  const { posId: rawPos } = useParams();
  const user = ls.get("user");
  const userPosId = user?.posId || "";
  const storeId = resolvePosId(rawPos);
  const resolvedParamId = getRouteSegmentForPosId(rawPos);
  const normalizedUserId = getRouteSegmentForPosId(userPosId);
  const [checkingStoreMode, setCheckingStoreMode] = useState(true);
  const [localOnlyTarget, setLocalOnlyTarget] = useState("");
  const [localOnlyBlocked, setLocalOnlyBlocked] = useState(false);

  const mismatch =
    Boolean(normalizedUserId) &&
    Boolean(resolvedParamId) &&
    normalizedUserId !== resolvedParamId;

  useEffect(() => {
    let active = true;

    const checkStoreMode = async () => {
      if (mismatch || !storeId || !process.env.REACT_APP_BACKEND_URL) {
        if (active) {
          setLocalOnlyBlocked(false);
          setLocalOnlyTarget("");
          setCheckingStoreMode(false);
        }
        return;
      }

      try {
        if (active) setCheckingStoreMode(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/stores/${storeId}/settings`
        );
        if (!active) return;

        const mode = data?.settings?.mode || "";
        const localIp = data?.settings?.localIp || "";
        const routeSegment = getRouteSegmentForPosId(storeId) || rawPos || storeId;
        const expectedLocalHost = normalizeLocalHost(localIp);
        const currentHost = normalizeLocalHost(window.location.hostname);
        const shouldBlockLocalOnly =
          mode === "LOCAL_FIRST" &&
          Boolean(expectedLocalHost) &&
          expectedLocalHost !== currentHost;

        setLocalOnlyBlocked(shouldBlockLocalOnly);
        setLocalOnlyTarget(
          shouldBlockLocalOnly
            ? buildLocalAssistedUrl({
                localIp,
                routeSegment,
                wantsOrder,
                isMobile,
              })
            : ""
        );
      } catch (error) {
        if (!active) return;
        setLocalOnlyBlocked(false);
        setLocalOnlyTarget("");
      } finally {
        if (active) setCheckingStoreMode(false);
      }
    };

    checkStoreMode();
    return () => {
      active = false;
    };
  }, [isMobile, mismatch, rawPos, storeId, wantsOrder]);

  if (mismatch) {
    return (
      <RouteSuspense>
        <StoreRedirect
          allowedPosId={normalizedUserId}
          wantsOrder={wantsOrder}
          isMobile={isMobile}
        />
      </RouteSuspense>
    );
  }

  if (checkingStoreMode) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Checking store access...</Typography>
      </Box>
    );
  }

  if (localOnlyBlocked) {
    return (
      <RouteSuspense>
        <StoreRedirect
          mode="localOnly"
          localTarget={localOnlyTarget}
        />
      </RouteSuspense>
    );
  }

  return children;
};

export default StoreGuard;
